/**
 * Pré-carga de chunks de rota (17/09/2026, rodada de performance).
 *
 * Toda página é `lazy()` no `AppRoutes`, e o `import()` de um chunk só começa
 * quando a rota é RENDERIZADA — depois do toque, e depois do boot. Medido:
 *  · abrir um perfil pela liga baixava ~85 KB em ~12 requisições atrás de um
 *    spinner de tela cheia, ANTES de qualquer leitura de dado começar;
 *  · a Home (30 KB gzip) só começava a baixar depois que a sessão e o perfil
 *    tinham assentado — duas esperas em série que podiam ser uma.
 *
 * Um `import()` do mesmo especificador resolve para o MESMO módulo (o Vite gera
 * um chunk só por página), então disparar a importação cedo faz o `lazy()`
 * assentar de imediato quando a rota chega. Cada função é idempotente e falha em
 * silêncio: a rota faz o próprio `import()` na hora, e o `chunkReload` (BUG-37)
 * cuida de chunk obsoleto.
 *
 * Nos testes (`MODE === "test"`) nada é pré-carregado: importar uma página
 * inteira dentro de um teste de guard traria o grafo dela sem os mocks dele.
 */

type Loader = () => Promise<unknown>;

function once(load: Loader): () => void {
  let started = false;
  return () => {
    if (started || import.meta.env.MODE === "test") return;
    started = true;
    void load().catch(() => {
      started = false;
    });
  };
}

/** Roda quando o navegador estiver ocioso — ou em 2 s, o que vier primeiro. */
function whenIdle(run: () => void): void {
  if (typeof window === "undefined") return;
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(run, { timeout: 2_000 });
  } else {
    setTimeout(run, 300);
  }
}

const home = once(() => import("../pages/Home"));
const login = once(() => import("../pages/Login"));
const publicProfile = once(() => import("../pages/PublicProfile"));
const bookDetail = once(() => import("../pages/BookDetail"));

/**
 * A Home é o destino de toda sessão que abre em `/` ou num deep link. Chamado
 * assim que o `AuthContext` sabe que HÁ sessão — antes de o perfil chegar —,
 * para o download do chunk correr junto com a leitura do perfil. Imediato, não
 * ocioso: o boot nunca está ocioso, e o que se quer é sobrepor as esperas.
 */
export function prefetchHomeChunk(): void {
  home();
}

/**
 * Login — a rota pública espera o primeiro `onAuthStateChanged` (que na web
 * fica atrás do iframe do Google) ANTES de renderizar o `lazy()`: medido em
 * 17/09, o chunk só começava a baixar ~1 s depois de o entry ter terminado.
 * Chamado pelo guard enquanto a sessão resolve.
 */
export function prefetchLoginChunk(): void {
  login();
}

/** Perfil público — quem desenha linhas de ranking/feed chama isto no mount. */
export function prefetchPublicProfileChunk(): void {
  whenIdle(publicProfile);
}

/**
 * BookDetail — a página mais visitada e o destino de todo resultado de busca.
 * O chunk tem ~132 KB brutos e só começava a baixar no toque, DEPOIS da
 * espera da busca (4ª de 17/09). Chamado quando uma lista de resultados
 * chega: o download corre enquanto a pessoa escolhe o livro.
 */
export function prefetchBookDetailChunk(): void {
  whenIdle(bookDetail);
}
